import { createContext, useContext } from "react";
import Router from "next/router";
import { useClerk } from "@clerk/nextjs";
import { usePostHog } from "posthog-js/react";
import type { RouterOutputs } from "@remi/api";
import { splitFullName } from "@remi/formatting";
import { RemiButton, useEffect } from "@remi/next-ui";
import {
  clearPostHogContextInSentry,
  clearUserInSentry,
  setPostHogContextInSentry,
  setUserInSentry,
  syncPostHogSessionIdToCookie,
} from "@remi/sentry";
import { REMI_INTERNAL_ORG } from "@remi/types/organizations";

import { api } from "~/utils/api";
import { clearAllCache } from "~/utils/cache";
import Loading from "~/components/Loading";

type UserProfile = RouterOutputs["auth"]["me"];

/**
 * The user will only be set to this value if the hook is used outside the UserProfileProvider
 * This provider wraps the whole app from the _app.tsx file
 */
const FAKE_USER_FOR_MISSING_PROVIDER: UserProfile = {
  personId: "fake-user-for-profile-context",
  clerkId: "fake-user",
  createdAt: new Date(),
  imposterId: undefined,
  person: {
    id: "fake-user-for-profile-context",
    firstName: "",
    lastName: "",
    name: "",
    contact: {
      id: "",
      name: "",
      preferredContactMethod: null,
      preferredLanguage: "ENGLISH",
    },
    canMention: true,
    coveringPerson: null,
    coveringPersonId: null,
    disabledById: null,
    createdAt: new Date(),
    deletedAt: null,
    disabled: true,
    organizationId: "fake-org",
    permissionRoles: [],
    regionRoles: [],
    capabilities: [],
    personGroups: [],
    contractorProfileRoles: [],
    phoneType: null,
    phones: [],
    preferredContactMethod: null,
    preferredLanguage: "ENGLISH",
    primaryPhone: null,
    primaryPhoneId: null,
    profilePhotoId: null,
    profilePhotoUrl: null,
    setStatusEvent: null,
    setStatusEventId: null,
    status: "ACTIVE",
    timeZone: null,
    jobTitle: null,
    updatedAt: new Date(),
    primaryEmailAddressId: null,
    primaryEmailAddress: null,
    organization: {
      id: "fake-org",
      jnid: null,
      createdAt: new Date(),
      name: "Fake Org",
      quickbooksCustomerId: null,
      type: "HOMEOWNER",
      updatedAt: new Date(),
      deletedAt: null,
      vendorId: null,
      discountPercentage: 0,
      contactId: "",
      contact: { id: "", name: "Fake Org" },
    },
    contactId: "",
    manuallyEdited: false,
    receivesDailyDigest: false,
  },
};

const UserProfileContext = createContext<{ user: UserProfile }>({
  user: FAKE_USER_FOR_MISSING_PROVIDER,
});

export function useUserProfile() {
  return useContext(UserProfileContext);
}

export const UserProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const utils = api.useUtils();

  const clerk = useClerk();
  const posthog = usePostHog();

  const {
    data: user,
    isPending,
    isError,
  } = api.auth.me.useQuery(undefined, {
    staleTime: 1000 * 60 * 5,
    // The identity is persisted to IndexedDB, so without this a refresh serves stale groups/roles/
    // permissions from cache; revalidate on mount so role and access changes take effect on next load.
    refetchOnMount: "always",
  });

  useEffect(() => {
    if (!user) {
      return;
    }

    const { imposterId, person } = user;
    const {
      id,
      contact,
      contractorProfileRoles,
      jobTitle,
      organization,
      organizationId,
      permissionRoles,
      personGroups,
      primaryEmailAddress,
    } = person;
    const [firstName, lastName] = splitFullName(contact.name);

    // PostHog identification
    posthog.identify(id, {
      email: primaryEmailAddress?.emailAddress,
      firstName,
      lastName,
      organizationId,
      organizationName: organization.name,
      organizationType: organization.type,
      isRemiInternal: organizationId === REMI_INTERNAL_ORG.id,
      jobTitle,
      personGroups: personGroups.map(({ group }) => group.key),
      permissionRoles,
      contractorProfileRoles: contractorProfileRoles.map(({ role }) => role),
    });

    // Event-level, not a person property: identify() runs as the impersonated
    // person, so persisting this would brand that real user as impersonated for
    // good. Rage-click reports filter it out to keep staff clicks out of the numbers.
    posthog.register({ isImpersonated: Boolean(imposterId) });

    // Sync PostHog session ID to our cookie for backend access
    const sessionId = posthog.get_session_id();
    if (sessionId) {
      syncPostHogSessionIdToCookie(sessionId);
      setPostHogContextInSentry(sessionId);
    }

    setUserInSentry({
      id,
      firstName,
      lastName,
      imposterId,
      permissionRoles,
      organizationId,
    });
  }, [user, posthog]);

  /**
   * Handlers
   */

  const handleSignOut = async () => {
    clearUserInSentry();
    clearPostHogContextInSentry();
    // Super properties live in localStorage, so isImpersonated would otherwise
    // follow the next person to sign in on a shared workstation.
    posthog.reset();
    // reset() rotates the session immediately, but the backend correlation cookie
    // lives for 10 minutes — without this re-sync a logout/login inside that window
    // sends the old session id to the backend while the frontend emits under the new one.
    const resetSessionId = posthog.get_session_id();
    if (resetSessionId) {
      syncPostHogSessionIdToCookie(resetSessionId);
    }
    await utils.invalidate();
    await clearAllCache();
    await clerk.signOut(() => void Router.push("/"));
  };

  /**
   * Render
   */

  if (isError) {
    return (
      <div className="grid min-h-full w-full place-items-center bg-background-primary px-6 py-24 sm:py-32 lg:px-8">
        <div className="text-center">
          <p className="font-semibold text-base text-primary">401</p>
          <h1 className="mt-4 font-bold text-3xl text-text-primary tracking-tight sm:text-5xl">
            Could not authenticate
          </h1>
          <p className="mt-6 text-base text-text-tertiary leading-7">
            There was an issue fetching your account. Our development team has been notified. Please try again later.
          </p>
          <div className="mt-10 flex items-center justify-center gap-x-6">
            <RemiButton muted onClick={handleSignOut}>
              Sign out
            </RemiButton>
          </div>
        </div>
      </div>
    );
  }

  if (isPending) {
    return <Loading />;
  }

  return <UserProfileContext.Provider value={{ user }}>{children}</UserProfileContext.Provider>;
};
